import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Camera, Loader2, X } from 'lucide-react';
import { uploadManualApplicationPhoto } from '../../../../../api/register';
import type { RegisterFormData } from '../../../../../stores/registerStore';
import { maskIdCard } from '../mask';

/** 人工申请弹窗 */
export function ManualApplicationModal({
  isOpen,
  onClose,
  formData,
  reason,
  setReason,

  onPhotoChange,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  formData: RegisterFormData;
  reason: string;
  setReason: (reason: string) => void;
  /** 已上传身份证照片的相对 URL（null 表示未上传） */
  photoUrl: string | null;
  onPhotoChange: (photoUrl: string | null) => void;
  onSubmit: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // 关闭时释放本地预览
  useEffect(() => {
    if (!isOpen && previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/image\/(jpeg|jpg|png|webp|bmp)/.test(file.type)) {
      alert('仅支持 JPG/PNG/WEBP/BMP 图片');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('照片不能超过 5MB');
      return;
    }
    setUploading(true);
    try {
      const url = await uploadManualApplicationPhoto(file);
      onPhotoChange(url);
      setPreviewUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(file);
      });
    } catch (error) {
      alert(error instanceof Error ? error.message : '照片上传失败，请稍后重试');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    onPhotoChange(null);
    setPreviewUrl((old) => {
      if (old) URL.revokeObjectURL(old);
      return null;
    });
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[480px] mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <FileText className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">人工身份验证申请</h3>
              <p className="text-sm text-slate-500">自动验证失败，请提交人工审核</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-slate-700 mb-3">您的信息</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">姓名</span>
                <span className="text-slate-800">{formData.fullName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">手机号</span>
                <span className="text-slate-800">{formData.phone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">邮箱</span>
                <span className="text-slate-800">{formData.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">身份证号</span>
                <span className="text-slate-800">{maskIdCard(formData.idCard)}</span>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              身份证照片 <span className="text-slate-400 text-xs">（选填，上传后便于快速审核）</span>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/bmp"
              className="hidden"
              onChange={handleFileChange}
            />
            {previewUrl ? (
              <div className="relative rounded-lg overflow-hidden border border-slate-200">
                <img src={previewUrl} alt="身份证照片预览" className="w-full h-40 object-cover" />
                {uploading && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
                {!uploading && (
                  <button
                    onClick={handleRemovePhoto}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                    title="移除照片"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full border-2 border-dashed border-slate-200 rounded-lg py-6 flex flex-col items-center gap-2 text-slate-500 hover:border-teal-400 hover:text-teal-600 transition-colors disabled:opacity-60"
              >
                {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Camera className="w-6 h-6" />}
                <span className="text-sm">{uploading ? '上传中...' : '点击上传身份证照片（5MB 以内）'}</span>
              </button>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              申请原因 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-colors resize-none"
              rows={4}
              placeholder="请说明为什么需要人工审核，例如：身份信息有变更、系统验证失败等"
            />
          </div>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-700">
              提交申请后，工作人员将在1-3个工作日内进行审核，审核结果将通过邮箱通知您（短信通知需平台开启）。
            </p>
          </div>
        </div>
        <div className="p-6 border-t border-slate-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            取消
          </button>
          <button
            onClick={onSubmit}
            disabled={!reason.trim() || uploading}
            className="flex-1 py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 text-white text-sm font-medium rounded-lg transition-colors"
          >
            提交申请
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}